import SyosEvents from './utils/SyosEvents';
import SyosUiContainer from './SyosUiContainer';
import { srOnlyElement, srOnlySpan } from '../utils/screen-reader/html';
import SvgIcon from '../utils/SvgIcon';
import IconSprites from '../utils/IconSprites';
import type SyosController from './SyosController';
import type SyosCartContainer from './SyosCartContainer';
import { IPriceTypeSelectionHelper } from './priceTypeSelectionHelpers/types';
import { ISyosLabels } from './labels/types';

const ID_CART_SUMMARY = 'tn-syos-cart-summary';

const CLASS_BASE = 'tn-syos-mobile-cart-controls';
const CLASS_BTN_VIEW_CART = `${CLASS_BASE}__btn-view-cart`;
const CLASS_BTN_VIEW_CART_LABEL = `${CLASS_BTN_VIEW_CART}-label`;
const CLASS_BTN_ADD_TO_CART = 'tn-syos__btn-add-to-cart';

export function getComponentElements(rootHeadingLevel: number, labels: ISyosLabels) {
  const headingTag = `h${rootHeadingLevel}`;

  const el = document.createElement('div');

  el.innerHTML = `
    ${srOnlyElement(headingTag, labels.selectedSeatsHeader)}
    <button type="button" class="btn btn-default ${CLASS_BTN_VIEW_CART}">
      ${SvgIcon.getHtml(IconSprites.CartFill, `${CLASS_BTN_VIEW_CART}-icon`)}
      <span class="${CLASS_BTN_VIEW_CART_LABEL}">View Seats</span>
    </button>

    <div id="${ID_CART_SUMMARY}" class="${CLASS_BASE}__summary">
      <span class="${CLASS_BASE}__summary-subtotal"></span>
      ${srOnlySpan(', ')}
      <span class="${CLASS_BASE}__summary-seat-count"></span>
    </div>

    <button type="button" aria-describedby="${ID_CART_SUMMARY}" class="btn btn-primary btn-lg ${CLASS_BTN_ADD_TO_CART}">
      ${labels.addToCartButton}
    </button>
  `;

  return {
    el,
    heading: el!.firstElementChild!,
  };
}

class SyosMobileCartControls extends SyosUiContainer {
  $btnToggleCart: JQuery<HTMLElement> | undefined;

  $btnAddToCart: JQuery<HTMLElement> | undefined;

  $subtotal: JQuery<HTMLElement> | undefined;

  $seatCount: JQuery<HTMLElement> | undefined;

  constructor(syosController: SyosController, rootHeadingLevel: number, private priceTypeSelectionHelper: IPriceTypeSelectionHelper, private labels: ISyosLabels) {
    super(syosController, `.${CLASS_BASE}`);

    const { el } = getComponentElements(rootHeadingLevel, this.labels);

    [...el.childNodes].forEach((c) => this.$el?.append(c));

    this.$btnToggleCart = this.find(`.${CLASS_BTN_VIEW_CART}`);
    this.$btnAddToCart = this.find(`.${CLASS_BTN_ADD_TO_CART}`);

    this.$subtotal = this.find('.tn-syos-mobile-cart-controls__summary-subtotal');
    this.$seatCount = this.find('.tn-syos-mobile-cart-controls__summary-seat-count');

    this.$btnToggleCart?.on('click', () => {
      this.syosController?.emit(SyosEvents.CART_DETAILS_OPEN);
    });

    this.$btnAddToCart?.on('click', () => {
      this.syosController?.emit(SyosEvents.RESERVE_SEATS);
    });

    this.syosController?.on(SyosEvents.CART_UPDATE, this.onCartUpdate);

    this.update();
  }

  onCartUpdate = ({ cart }: { cart: SyosCartContainer }) => {
    this.update();

    const seatCount = cart.getSeatCount();

    if (seatCount === 0) {
      window.setTimeout(() => {
        if (document.activeElement?.tagName !== 'g') {
          this.$el?.focus();
        }
      });
    }
  };

  update() {
    const cart = this.syosController!.getCartContainer()!;

    const seatCount = cart.getSeatCount();

    let seatCountLabel: string;

    if (this.priceTypeSelectionHelper.mode === 'flex') {
      const { selectedSeatsLabel, selectedSeatsSeparatorText, remainingSeatsLabel } = this.labels;

      const { selectionsRemaining } = this.priceTypeSelectionHelper;

      seatCountLabel = `${seatCount} ${selectedSeatsLabel}${selectedSeatsSeparatorText} ${selectionsRemaining} ${remainingSeatsLabel}`;
    } else {
      const seatOrSeats = seatCount === 1 ? 'Seat' : 'Seats';
      seatCountLabel = `${seatCount} ${seatOrSeats}`;
    }

    this.$seatCount?.html(seatCountLabel);

    this.$subtotal?.html(cart.getSubtotalDisplay());

    const isToggleCartDisabled = seatCount === 0;

    const isAddToCartDisabled = !this.priceTypeSelectionHelper.isSelectionComplete;

    this.$btnToggleCart?.prop('disabled', isToggleCartDisabled);

    this.$btnAddToCart?.prop('disabled', isAddToCartDisabled);
  }

  destroy() {
    this.$btnToggleCart?.off();
    this.$btnAddToCart?.off();

    super.destroy();
  }
}

export default SyosMobileCartControls;

export { ID_CART_SUMMARY };
